import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import {ScrollableFlex} from '../../utils/scrolling';
import {WrapperFlex} from '../../utils/scrolling';
import {
  LeftColumnContent,
  RightColumnContent,
  TwoColumnGrid,
} from './components';
import {AgentBuilderPageIndicator} from './AgentBuilderPageIndicator';
import {useAgentNavigation} from './AgentBuilderNav.state';
import {useFieldDefinitionEditorStore} from './AiFieldDefinitionEditor.state';
import {
  useAgentBuilderStore,
  useAgentBuilderServerState,
} from './AgentBuilder.state';
import Select from 'react-select';
import {assertNever, StrictExclude} from '../../../shared/util';
import {AnalystFieldConfigEditor} from './AnalystFieldConfigEditor';
import {BuilderFooter} from './BuilderFooter';
import {useAgentAnalystField, useAgentResearcherField} from './helpers';
import {ConfigFormLabel} from './ConfigFormLabel';
import {BuilderPreviewTable} from './BuilderPreviewTable';
import {AgentBuilderPreviewButton} from './AgentBuilderPreviewButton';
import {useIsEnrichingAgent} from '../../../hooks/api/enrichment';
import {AiFieldType} from '../../../shared/fields/enrichment';
import {
  LabelAndDescriptionData,
  LabelAndDescriptionOption,
} from '../../LabelAndDescriptionOption';
import {defaultSelectStyles} from '../../theme/select';
import {StepContainer} from './StepContainer';
import {getAiFieldModel} from '../../../shared/agent';
export const AgentBuilderEditAnalystFieldStep = () => {
  const {id: agentId, status: agentStatus} = useAgentBuilderServerState();
  const {previewAccountIds} = useAgentBuilderStore();
  const {config, isSaveable, publishChanges, updateServerState} =
    useFieldDefinitionEditorStore();
  const agentAnalystField = useAgentAnalystField(agentId);

  useEffect(() => {
    if (agentAnalystField) {
      updateServerState({
        serverState: agentAnalystField,
        updateMode: 'ifNoChanges',
      });
    }
  }, [agentAnalystField, updateServerState]);

  const onNavigate = useAgentNavigation({
    mutation: publishChanges,
    saveOnBack: agentStatus === 'draft' && isSaveable(),
  });

  return (
    <StepContainer isSaveable={isSaveable()} onSaveMutation={publishChanges}>
      <TwoColumnGrid>
        <Flex
          borderRight="1px solid"
          borderColor="kgray.200"
          direction="column"
        >
          <Box position="relative" h="100%">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <LeftColumnContent>
                  <AgentBuilderPageIndicator />
                  <AnalystFieldEditor />
                </LeftColumnContent>
              </ScrollableFlex>
            </WrapperFlex>
          </Box>

          <Divider />

          <BuilderFooter justifyContent="space-between">
            <Button
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('prev')}
              variant="ghost"
            >
              Back
            </Button>

            <Button
              colorScheme="kbuttonblue"
              isDisabled={!isSaveable()}
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('next')}
            >
              {config?.fieldType === 'estimate'
                ? 'Continue to Review'
                : 'Continue to Test'}
            </Button>
          </BuilderFooter>
        </Flex>

        <RightColumnContent>
          <Flex alignItems="baseline" justifyContent="space-between" mt="-6px">
            <Text fontSize="xl">Preview</Text>
            <AgentBuilderPreviewButton accountIds={previewAccountIds} />
          </Flex>

          <Box flex="1" position="relative">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <BuilderPreviewTable
                  accountIds={previewAccountIds}
                  fieldDefinition={agentAnalystField}
                  isPreviewDisabled={!isSaveable()}
                />
              </ScrollableFlex>
            </WrapperFlex>
          </Box>
        </RightColumnContent>
      </TwoColumnGrid>
    </StepContainer>
  );
};

type AnalystFieldType = StrictExclude<
  AiFieldType,
  'research' | 'legacyResearch' | 'legacyTeamSize' | 'teamSize'
>;
type AnalystFieldTypeData = LabelAndDescriptionData<AnalystFieldType>;

const analystFieldTypeOptions = [
  {
    label: '1-10 Rating',
    description: 'For questions like "How likely is..." or "How much does..."',
    value: 'rating',
  },
  {
    label: 'Estimate',
    description:
      'For questions like "How many..." or "What is the estimated..."',
    value: 'estimate',
  },
  {
    label: 'Categories',
    description: 'For questions like "Which types..."',
    value: 'category',
  },
  {
    label: 'True/False',
    description:
      'For questions like "Does this company..." or "Is this company..."',
    value: 'boolean',
  },
] satisfies AnalystFieldTypeData[];

const AnalystFieldTypeOption = LabelAndDescriptionOption<AnalystFieldType>;
const analystFieldTypeSelectStyles = defaultSelectStyles<
  AnalystFieldTypeData,
  false
>();

const createDefaultConfig = (
  fieldType: AnalystFieldType,
  agentResearcherField: ReturnType<typeof useAgentResearcherField>
) => {
  const commonConfig = {
    analysisFields: agentResearcherField
      ? [{fieldId: agentResearcherField.id, hash: ''}]
      : [],
    model: getAiFieldModel(),
  };

  switch (fieldType) {
    case 'boolean':
      return {
        ...commonConfig,
        fieldType,
        prompt: '',
      };
    case 'category':
      return {
        ...commonConfig,
        fieldType,
        categories: [],
        categoryName: '',
        maxCategories: 1,
      };
    case 'rating':
      return {
        ...commonConfig,
        fieldType,
        min: 1,
        max: 10,
        prompt: '',
      };
    case 'estimate':
      return {
        ...commonConfig,
        fieldType,
        prompt: '',
      };
    default:
      return assertNever(fieldType);
  }
};

const TypeSwitchWarningModal = ({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      returnFocusOnClose={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Switch Analyst Type?</ModalHeader>
        <ModalCloseButton />
        <ModalBody py="4">
          <Text fontSize="md">
            Switching analyst types will remove all your current work and reset
            the configuration.
          </Text>
        </ModalBody>
        <ModalFooter gap="4">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            colorScheme="red"
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            Switch Type
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const AnalystFieldEditor = () => {
  const {config, setConfig, isSaveable} = useFieldDefinitionEditorStore();
  const {id: agentId, status} = useAgentBuilderServerState();
  const agentResearcherField = useAgentResearcherField(agentId);
  const {data: isEnrichingAgent} = useIsEnrichingAgent({agentId});
  const isDisabled = status === 'published' || isEnrichingAgent;
  const [warningModal, setWarningModal] = useState<{
    isOpen: boolean;
    pendingFieldType?: AnalystFieldType;
  }>({
    isOpen: false,
  });

  const handleConfirmTypeSwitch = () => {
    const fieldType = warningModal.pendingFieldType;
    if (!fieldType) {
      return;
    }

    setConfig(createDefaultConfig(fieldType, agentResearcherField));
  };

  return (
    <Flex direction="column" gap="6">
      <Text fontSize="xl">Prompt your Analyst</Text>

      <FormControl isRequired>
        <ConfigFormLabel>Analyst type</ConfigFormLabel>
        <Select
          components={{
            Option: AnalystFieldTypeOption,
          }}
          isDisabled={isDisabled}
          isMulti={false}
          isSearchable={false}
          menuPlacement="auto"
          onChange={(newValue) => {
            const fieldType = newValue?.value;
            if (!fieldType || fieldType === config?.fieldType) {
              return;
            }

            if (isSaveable()) {
              setWarningModal({
                isOpen: true,
                pendingFieldType: fieldType,
              });
            } else {
              setConfig(createDefaultConfig(fieldType, agentResearcherField));
            }
          }}
          options={analystFieldTypeOptions}
          placeholder="Select a type"
          styles={analystFieldTypeSelectStyles}
          value={analystFieldTypeOptions.find(
            (option) => option.value === config?.fieldType
          )}
        />
      </FormControl>

      <AnalystFieldConfigEditor />

      <TypeSwitchWarningModal
        isOpen={warningModal.isOpen}
        onClose={() => setWarningModal({isOpen: false})}
        onConfirm={handleConfirmTypeSwitch}
      />
    </Flex>
  );
};
